import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  message,
  Row,
  Col,
  Typography,
  Space,
  Statistic,
  Upload,
  Select,
  Divider,
  Spin,
  InputNumber,
  Progress
} from 'antd';
import { 
  ArrowLeftOutlined, 
  CheckCircleOutlined,
  UploadOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from '../../utils/axios';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

const AccountRepayment = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [accountInfo, setAccountInfo] = useState(null);
  const [loadingAccount, setLoadingAccount] = useState(true);
  const [fileList, setFileList] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [paymentReference, setPaymentReference] = useState('');
  const [repaymentAmount, setRepaymentAmount] = useState(0);
  const [repaymentMode, setRepaymentMode] = useState('full'); // 'full', 'partial', 'minimum'
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchAccountInfo();
  }, []);

  const fetchAccountInfo = async () => {
    try {
      setLoadingAccount(true);
      const response = await axios.get('/client/balance');
      setAccountInfo(response.data);
      
      // Set default repayment amount to full
      if (response.data.creditUsed > 0) {
        setRepaymentAmount(response.data.creditUsed);
      }
    } catch (error) {
      console.error('Failed to fetch account info:', error);
      message.error('Failed to load account information');
    } finally {
      setLoadingAccount(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return '0.00';
    const currency = accountInfo?.currency || 'USD';
    if (currency === 'HKD') return `HK$${Number(amount).toFixed(2)}`;
    if (currency === 'USD') return `$${Number(amount).toFixed(2)}`;
    return `${currency} ${Number(amount).toFixed(2)}`;
  };

  const handleRepaymentModeChange = (mode) => {
    setRepaymentMode(mode);
    
    if (!accountInfo) return;
    
    switch (mode) {
      case 'full':
        setRepaymentAmount(accountInfo.creditUsed);
        break;
      case 'minimum':
        // Min repayment: 10% of total due, min $50
        const minimumAmount = Math.max(accountInfo.creditUsed * 0.1, 50);
        setRepaymentAmount(Number(minimumAmount.toFixed(2)));
        break;
      case 'partial':
        // Custom amount, keep current value
        break;
      default:
        break;
    }
  };

  const validateRepaymentAmount = () => {
    if (!repaymentAmount || repaymentAmount <= 0) {
      message.error('Please enter a valid repayment amount');
      return false;
    }
    
    if (repaymentAmount > accountInfo.creditUsed) {
      message.error('Repayment amount cannot exceed outstanding balance');
      return false;
    }
    
    const minimumAmount = Math.max(accountInfo.creditUsed * 0.1, 50);
    if (repaymentAmount < minimumAmount) {
      message.error(`Minimum repayment amount is ${formatCurrency(minimumAmount)}`);
      return false;
    }
    
    return true;
  };

  const handleSubmitRepayment = async () => {
    if (!validateRepaymentAmount()) return;
    if (!paymentReference || !paymentReference.trim()) {
      message.error('Payment reference is required.');
      return;
    }

    try {
      setIsSubmitting(true);

      const formData = new FormData();
      formData.append('amount', repaymentAmount);
      formData.append('paymentMethod', paymentMethod);
      formData.append('paymentReference', paymentReference.trim());
      // Backend reads `repaymentType` (defaulting to 'partial') — sending
      // `type: 'repayment'` previously was silently ignored, so full
      // repayments were always recorded as partial.
      const isFullRepayment =
        Number(repaymentAmount) >= Number(accountInfo?.creditUsed || 0);
      formData.append('repaymentType', isFullRepayment ? 'full' : 'partial');
      
      if (fileList.length > 0) {
        formData.append('paymentProof', fileList[0].originFileObj);
      }
      
      await axios.post('/client/repayment', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      message.success('Repayment submitted successfully! Awaiting approval.');
      
      // Reset form
      form.resetFields();
      setFileList([]);
      setPaymentReference('');
      
      // Refresh account info
      fetchAccountInfo();
      
      // Return to main page
      setTimeout(() => {
        navigate('/client/dashboard');
      }, 2000);
      
    } catch (error) {
      console.error('Repayment submission failed:', error);
      message.error(error.response?.data?.message || 'Failed to submit repayment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const uploadProps = {
    fileList,
    beforeUpload: (file) => {
      const isValidType = file.type.startsWith('image/') || file.type === 'application/pdf';
      if (!isValidType) {
        message.error('Only images and PDF files are allowed!');
        return false;
      }
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('File must be smaller than 10MB!');
        return false;
      }
      setFileList([file]);
      return false; // Prevent automatic upload
    },
    onRemove: () => {
      setFileList([]);
    },
    maxCount: 1
  };

  if (loadingAccount) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <p style={{ marginTop: '20px' }}>Loading account information...</p>
      </div>
    );
  }

  if (!accountInfo?.paymentStatus?.hasOutstanding || !accountInfo.creditUsed || accountInfo.creditUsed <= 0) {
    return (
      <div style={{ padding: '20px' }}>
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <CheckCircleOutlined style={{ fontSize: '64px', color: '#52c41a', marginBottom: '20px' }} />
            <Title level={3}>No Outstanding Balance</Title>
            <Text type="secondary">You don't have any outstanding payments at this time.</Text>
            <div style={{ marginTop: '20px' }}>
              <Button type="primary" onClick={() => navigate('/client/dashboard')}>
                Back to Dashboard
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const creditUtilization = accountInfo.creditUtilization || 0;

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '20px' }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate('/client/dashboard')}
          style={{ marginRight: '16px' }}
        >
          Back to Dashboard
        </Button>
        <Title level={2} style={{ display: 'inline', margin: 0 }}>
          💳 Account Repayment
        </Title>
      </div>

      <Row gutter={[16, 16]}>
        {/* Account Summary */}
        <Col xs={24} lg={8}>
          <Card title="💰 Current Balance Overview" size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Statistic
                title="Outstanding Balance"
                value={accountInfo.creditUsed}
                prefix="$"
                valueStyle={{ color: '#cf1322' }}
                precision={2}
              />
              
              <Divider />
              
              <div>
                <Text strong>Credit Utilization</Text>
                <Progress 
                  percent={creditUtilization} 
                  strokeColor={creditUtilization > 75 ? '#cf1322' : creditUtilization > 50 ? '#faad14' : '#52c41a'}
                  format={percent => `${percent.toFixed(1)}%`}
                />
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>Credit Limit:</Text>
                <Text strong>{formatCurrency(accountInfo.creditLimit)}</Text>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>Available Credit:</Text>
                <Text strong style={{ color: '#52c41a' }}>
                  {formatCurrency(accountInfo.availableCredit)}
                </Text>
              </div>
              
              {accountInfo.paymentStatus?.nextPaymentDue && (
                <>
                  <Divider />
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>Next Due Date:</Text>
                    <Text strong>
                      {dayjs(accountInfo.paymentStatus.nextPaymentDue).format('MMM DD, YYYY')}
                    </Text>
                  </div>
                </>
              )}
            </Space>
          </Card>
        </Col>

        {/* Repayment Form */}
        <Col xs={24} lg={16}>
          <Card title="💸 Make a Payment" size="small">
            <Form form={form} layout="vertical" onFinish={handleSubmitRepayment}>
              {/* Repayment Mode Selection */}
              <Form.Item label="Payment Type">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button.Group>
                    <Button 
                      type={repaymentMode === 'full' ? 'primary' : 'default'}
                      onClick={() => handleRepaymentModeChange('full')}
                    >
                      Full Payment ({formatCurrency(accountInfo.creditUsed)})
                    </Button>
                    <Button 
                      type={repaymentMode === 'minimum' ? 'primary' : 'default'}
                      onClick={() => handleRepaymentModeChange('minimum')}
                    >
                      Minimum Payment
                    </Button>
                    <Button 
                      type={repaymentMode === 'partial' ? 'primary' : 'default'}
                      onClick={() => handleRepaymentModeChange('partial')}
                    >
                      Custom Amount
                    </Button>
                  </Button.Group>
                </Space>
              </Form.Item>

              {/* Payment Amount */}
              <Form.Item 
                label="Payment Amount" 
                required
                help={repaymentMode === 'minimum' ? 
                  `Minimum payment is ${formatCurrency(Math.max(accountInfo.creditUsed * 0.1, 50))}` : 
                  null
                }
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  max={accountInfo.creditUsed}
                  step={0.01}
                  precision={2}
                  value={repaymentAmount}
                  onChange={setRepaymentAmount}
                  formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  disabled={repaymentMode === 'full'}
                />
              </Form.Item>

              {/* Payment Method */}
              <Form.Item label="Payment Method" required>
                <Select 
                  value={paymentMethod} 
                  onChange={setPaymentMethod}
                  style={{ width: '100%' }}
                >
                  <Option value="bank_transfer">Bank Transfer</Option>
                  <Option value="wire_transfer">Wire Transfer</Option>
                  <Option value="check">Check</Option>
                  <Option value="online_payment">Online Payment</Option>
                </Select>
              </Form.Item>

              {/* Payment Reference */}
              <Form.Item
                label="Payment Reference/Transaction ID"
                required
                validateStatus={!paymentReference?.trim() && isSubmitting === false ? '' : undefined}
                help="Please provide the reference number from your payment"
              >
                <Input
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  placeholder="e.g., TXN123456789, Check #1234"
                  disabled={isSubmitting}
                />
              </Form.Item>

              {/* Payment Proof Upload */}
              <Form.Item 
                label="Payment Proof" 
                help="Upload receipt, bank statement, or proof of payment (optional but recommended)"
              >
                <Upload {...uploadProps}>
                  <Button icon={<UploadOutlined />}>
                    Upload Payment Proof
                  </Button>
                </Upload>
              </Form.Item>

              {/* Submit Button */}
              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isSubmitting}
                    disabled={isSubmitting}
                    size="large"
                    icon={<DollarOutlined />}
                  >
                    Submit Payment ({formatCurrency(repaymentAmount)})
                  </Button>
                  <Button
                    onClick={() => navigate('/client/dashboard')}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>

      {/* Payment Instructions */}
      <Card title="💡 Payment Instructions" style={{ marginTop: '20px' }} size="small">
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Title level={5}>Bank Transfer Details</Title>
            <Text>
              <strong>Account Name:</strong> Vouchain Ltd.<br/>
              <strong>Account Number:</strong> 123-456-7890<br/>
              <strong>Routing Number:</strong> 021000021<br/>
              <strong>Swift Code:</strong> CHASUS33<br/>
            </Text>
          </Col>
          <Col xs={24} md={12}>
            <Title level={5}>Important Notes</Title>
            <Text>
              • Include your client ID in the transfer memo<br/>
              • Keep your payment receipt for records<br/>
              • Processing typically takes 1-2 business days<br/>
              • Contact support for payment questions<br/>
            </Text>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default AccountRepayment;
